import type {
  TextColor,
  TextWeight,
} from 'src/modules/shared/components/atoms/text/text.styles';
import type { ComponentSize } from '../types/component.type';

/**
 * Shared dropdown and menu item styles
 * Used by Dropdown, SearchableSelect, and other dropdown-like components
 */

export const menuItemSizeClasses: Record<ComponentSize, string> = {
  lg: 'py-3 px-4 text-md',
  md: 'py-2 px-4 text-sm',
  sm: 'py-1.5 px-3 text-sm',
  xs: 'py-1 px-2 text-xs',
  '2xs': 'py-0.5 px-1.5 text-xs',
};

export const menuItemBaseClasses = [
  'flex',
  'items-center',
  'gap-2',
  'relative',
  'w-full',
  'appearance-none',
  'border-0',
  'cursor-pointer',
  'transition-colors',
  'rounded-md',
  'focus:outline-none',
  'focus-visible:outline-none',
  'focus:ring-0',
  'focus-visible:ring-0',
];

export const menuListClasses =
  'flex flex-col gap-1 p-1 max-h-80 overflow-y-auto z-[10000]';

/**
 * Generates CSS classes for menu items based on their state and size
 * @param size - The size variant of the menu item
 * @param isSelected - Whether the item is currently selected
 * @param isActive - Whether the item is in an active state
 * @param isDisabled - Whether the item is disabled
 * @returns Combined CSS classes string
 */
export const getMenuItemClasses = (
  size: ComponentSize,
  isSelected?: boolean,
  isActive?: boolean,
  isDisabled?: boolean
): string => {
  const backgroundClasses = getBackgroundClasses(isSelected, isActive);
  const disabledClasses = getDisabledClasses(isDisabled);

  return [
    ...menuItemBaseClasses,
    menuItemSizeClasses[size],
    backgroundClasses,
    disabledClasses,
  ].join(' ');
};

/**
 * Determines background classes based on item state
 * @param isSelected - Whether the item is selected
 * @param isActive - Whether the item is active
 * @returns Background CSS classes
 */
const getBackgroundClasses = (
  isSelected?: boolean,
  isActive?: boolean
): string => {
  if (isSelected) {
    return '!bg-primary-100 focus:!bg-primary-100 focus-visible:!bg-primary-100';
  }

  if (isActive) {
    return 'bg-secondary-100';
  }

  return 'hover:bg-secondary-100 focus:bg-secondary-100 focus-visible:bg-secondary-100';
};

/**
 * Determines disabled state classes
 * @param isDisabled - Whether the item is disabled
 * @returns Disabled CSS classes or empty string
 */
const getDisabledClasses = (isDisabled?: boolean): string => {
  return isDisabled
    ? 'opacity-50 cursor-not-allowed pointer-events-none hover:bg-transparent'
    : '';
};

export const getMenuItemContentClasses = (): string => {
  return 'text-left';
};

export const getMenuItemContentColor = (isSelected?: boolean): TextColor => {
  return isSelected ? 'primary' : 'secondary';
};

export const getMenuItemContentWeight = (isSelected?: boolean): TextWeight => {
  return isSelected ? 'regular' : 'light';
};

export const separatorClasses = 'my-1 border-blue-gray-50';
